import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  deleteField,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  SubmittedDocument,
  SubmittedDocuments,
} from "~/types/SubmittedDocument";
import { DocumentState } from "~/types/enums/DocumentState";

import { useDeleteTeacherSubmission } from "~/composables/useDeleteTeacherSubmission";
import { getRestoredDocumentState } from "~/utils/archived-documents";

import { useCollection } from "vuefire";
import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";

export const useDocuments = () => {
  const streamPendingUserDocuments = () => {
    const uid = useCurrentUID();

    if (!uid) {
      throw new Error("Unauthorized");
    }
    const db = useFirestore();

    const documentCollectionRef = collection(db, "/documents");
    const documentCollectionQuery = query(
      documentCollectionRef,
      where("userId", "==", uid),
      where("state", "in", [
        DocumentState.pending,
        DocumentState.notImported,
        DocumentState.conversionFailed,
        DocumentState.converting,
      ]),
      orderBy("lastUpdatedTimestamp", "desc")
    );

    return useCollection<SubmittedDocument>(documentCollectionQuery);
  };

  const streamUngradedUserDocuments = () => {
    const uid = useCurrentUID();

    if (!uid) {
      throw new Error("Unauthorized");
    }
    const db = useFirestore();
    const documentCollectionRef = collection(db, "/documents");
    const documentCollectionQuery = query(
      documentCollectionRef,
      where("userId", "==", uid),
      where("state", "not-in", [DocumentState.graded, DocumentState.archived])
    );

    return useCollection<SubmittedDocument>(documentCollectionQuery);
  };

  const streamGradedUserDocuments = () => {
    const uid = useCurrentUID();

    if (!uid) {
      throw new Error("Unauthorized");
    }
    const db = useFirestore();
    const documentCollectionRef = collection(db, "/documents");
    const documentCollectionQuery = query(
      documentCollectionRef,
      where("userId", "==", uid),
      where("state", "==", DocumentState.graded),
      orderBy("lastUpdatedTimestamp", "desc")
    );

    return useCollection<SubmittedDocument>(documentCollectionQuery);
  };

  const createDocument = async (
    name: string,
    params?: {
      userId: string;
      profileId?: string;
      classroom?: {
        id: string;
        name: string;
        enableNewStudentSubmitForm?: boolean;
        enableStudentReview?: boolean;
        enableSubmissionBadges?: boolean;
        enableStudentSubmissionCreation?: boolean;
        studentSubmissionCreationStudentIds?: string[];
        enableStudentCompare?: boolean;
        enableStudentThesaurus?: boolean;
        studentThesaurusStudentIds?: string[];
      };
      assignment?: {
        id: string;
        name: string;
        openTimestamp?: number;
        dueTimestamp?: number;
        closeTimestamp?: number;
        isVisibleToStudents?: boolean;
        allowKWOSubmission?: boolean;
        allowRoughDraftSubmission?: boolean;
        requiredKWOSubmission?: boolean;
        requireRoughDraftSubmission?: boolean;
        requireRoughDraftChecklist?: boolean;
        roughDraftSubmissionFormats?: string[];
        allowFinalDraftSubmission?: boolean;
        allowFinalDraftGradedState?: boolean;
        isAutoReturnEnabled?: boolean;
        type?: AssignmentType;
        levelId?: string;
        lessonId?: string;
      };
      student?: {
        id: string;
        name: string;
      };
      checklist?: {
        id: string;
        path: string;
      };
      allowFinalDraftGradedState?: boolean;
      documentSubmissionType?: DocumentSubmissionType;
      googleDriveFileId?: string;
      fileType?: string;
      documentState?: DocumentState;
      enableStudentThesaurus?: boolean;
      enableStudentReview?: boolean;
    }
  ) => {
    const submittedDocument = useCreateSubmittedDocumentFromParams({
      name,
      firestoreTimestamp: Timestamp.now(),
      params,
    });

    if (params?.documentState) {
      submittedDocument.state = params.documentState;
    }

    const db = useFirestore();
    const documentRef = collection(db, "/documents");
    const response = await addDoc(documentRef, submittedDocument);

    return response.id;
  };

  const deleteDocument = async (documentId: string, options?: { teacherDeletion?: boolean }) => {
    if (options?.teacherDeletion) return useDeleteTeacherSubmission(documentId);
    await updateDocumentStateById(documentId, DocumentState.archived);
  };

  const restoreDocument = async (documentId: string) => {
    const db = useFirestore();
    const documentRef = doc(db, `/documents/${documentId}`);
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(documentRef);
      if (!snapshot.exists()) throw new Error("Submission no longer exists.");
      const document = snapshot.data() as SubmittedDocument;
      if (document.state !== DocumentState.archived) return;
      transaction.update(documentRef, {
        state: getRestoredDocumentState(document),
        stateBeforeArchive: deleteField(),
        archivedAtTimestamp: deleteField(),
        lastUpdatedTimestamp: Date.now(),
      });
    });
  };

  const getDocumentsWithChecklistId = async (
    checklistId: string
  ): Promise<SubmittedDocuments> => {
    const db = useFirestore();
    const documentCollectionRef = collection(db, "/documents");
    const documentCollectionQuery = query(
      documentCollectionRef,
      where("checklistId", "==", checklistId)
    );

    const documents = await getDocs(documentCollectionQuery);

    return documents.docs.map((doc) => {
      return {
        id: doc.id,
        ...doc.data(),
      } as SubmittedDocument;
    });
  };

  const getLatestDocumentRevisionId = async (
    documentId: string
  ): Promise<string | undefined> => {
    const db = useFirestore();
    const revisionsRef = collection(db, `/documents/${documentId}/revisions`);
    const revisionsQuery = query(
      revisionsRef,
      orderBy("lastUpdatedTimestamp", "desc")
    );
    const revisionsSnapshot = await getDocs(revisionsQuery);

    return revisionsSnapshot.docs.find(d => d.data().isArchived !== true)?.id;
  };

  const updateDocument = async (document: SubmittedDocument) => {
    if (document.id == undefined) {
      throw new Error("Document id is undefined");
    }

    const db = useFirestore();
    const docRef = doc(db, `/documents/${document.id}`);
    await updateDoc(docRef, {
      ...document,
    });
  };

  const returnDocumentToStudent = async (documentId: string) => {
    const db = useFirestore();
    const docRef = doc(db, `/documents/${documentId}`);
    await updateDoc(docRef, { isReturnedToStudent: true });
  };

  const updateDocumentStateById = async (
    documentId: string,
    state: DocumentState
  ) => {
    const db = useFirestore();
    const docRef = doc(db, `/documents/${documentId}`);
    if (state === DocumentState.archived) {
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists()) throw new Error("Submission no longer exists.");
        const document = snapshot.data() as SubmittedDocument;
        if (document.state === DocumentState.archived) return;
        transaction.update(docRef, {
          state,
          stateBeforeArchive: document.state,
          archivedAtTimestamp: Date.now(),
          lastUpdatedTimestamp: Date.now(),
        });
      });
      return;
    }
    await updateDoc(docRef, { state });
  };

  const updateDocumentOriginalFileMediaHref = async (
    documentId: string,
    originalFileMediaHref: string
  ) => {
    const db = useFirestore();
    const docRef = doc(db, `/documents/${documentId}`);
    await updateDoc(docRef, {
      originalFileMediaHref: originalFileMediaHref,
    });
  };

  return {
    streamUngradedUserDocuments,
    streamPendingUserDocuments,
    streamGradedUserDocuments,
    createDocument,
    deleteDocument,
    restoreDocument,
    getDocumentsWithChecklistId,
    getLatestDocumentRevisionId,
    updateDocument,
    returnDocumentToStudent,
    updateDocumentStateById,
    updateDocumentOriginalFileMediaHref,
  };
};
